import { useCallback, useMemo, useState, type MouseEvent } from 'react';
import { css, type Theme } from '@emotion/react';
import { CaretDown, GitBranch, SealCheck, Tag as TagIcon, User } from '@phosphor-icons/react';
import { SearchableDropdown, type SearchableDropdownRenderOptionFunction } from 'client/components/Dropdown/SearchableDropdown';
import { ListItemText } from 'client/components/Other/ListItemText';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';

interface VersionOption {
  id: string;
  type: 'tag' | 'latest';
}

const renderVersionOption: SearchableDropdownRenderOptionFunction<VersionOption> = (props, option, { selected }) => {
  return (
    <li {...props} css={optionStyle}>
      {option.type === 'tag' && <TagIcon weight="bold" />}
      {option.type === 'latest' && <GitBranch weight="bold" />}
      <ListItemText primary={option.id} />
    </li>
  );
};

export interface StackComponentDialogDescriptionProps {
  component?: {
    identifier: string;
    version: string;
    details: {
      name: string;
      description: string;
      versions: string[];
      owner: {
        name: string;
        isVerified: boolean;
      };
      url: string;
    };
  } | null;
  onVersion: (version: string) => void;
}

export const StackComponentDialogDescription = ({ component, onVersion }: StackComponentDialogDescriptionProps) => {
  const [versionEl, setVersionEl] = useState<HTMLElement | null>(null);
  const version = component?.version || 'main';

  const handleVersionClick = useCallback((event: MouseEvent<HTMLButtonElement>) => setVersionEl(event.currentTarget), []);

  const handleVersionClose = useCallback(() => setVersionEl(null), []);

  const handleVersionChange = useCallback((option: VersionOption | null) => option && onVersion(option.id), [onVersion]);

  const versionOptions = useMemo<VersionOption[]>(
    () => [
      { id: 'main', type: 'latest' },
      ...(component?.details.versions || []).map((version) => ({ id: version, type: 'tag' as const })),
    ],
    [component?.details.versions]
  );

  return (
    <div css={infoStyle}>
      <Typography variant="body2">{component?.details.description || ''}</Typography>
      <Typography variant="body2" component="ul" css={infoFooterStyle}>
        <li>
          <Tag role="button" onClick={handleVersionClick} css={versionSelectStyle}>
            {version}
            <CaretDown weight="fill" />
          </Tag>
        </li>
        <li>
          {component?.details.owner.isVerified ? (
            <SealCheck weight="fill" size={16} color="var(--primary-color)" />
          ) : (
            <User weight="bold" size={16} />
          )}{' '}
          {component?.details.owner.name || ''}
        </li>
      </Typography>
      <SearchableDropdown
        anchorEl={versionEl}
        value={version || null}
        options={versionOptions}
        renderOption={renderVersionOption}
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
        onClose={handleVersionClose}
        onChange={handleVersionChange}
      />
    </div>
  );
};

const infoStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  max-width: 100%;

  > span {
    display: flex;
    align-items: center;
    margin-top: -3px;
    margin-bottom: 1rem;
    width: 100%;

    > svg {
      flex-shrink: 0;
      margin-right: 4px;
    }
  }
`;

const infoFooterStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  margin-top: 4px;
  padding: 0;
  list-style: none;

  --primary-color: ${theme.color.primary[100]};

  > li {
    display: flex;
    align-items: center;

    &:not(:last-of-type):not(:first-of-type):after {
      content: '·';
    }

    > svg {
      margin-right: 4px;
    }
  }
`;

const versionSelectStyle = css`
  padding: 4px 8px;
  cursor: pointer;

  > svg {
    margin-left: 4px;
  }
`;

const optionStyle = (theme: Theme) => css`
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;

  > svg {
    margin-right: 8px;
    color: ${theme.color.foreground};
  }

  > div {
    > .body1 {
      font-weight: bold;
    }
  }
`;
